import React, { useRef, useEffect } from 'react';
import '../styles/print.css';

/**
 * PrintTicket - Componente reutilizable para tickets térmicos 80mm
 * 
 * @param {Object} props
 * @param {string} props.type - "order" | "shipment"
 * @param {string} props.warehouseName - Nombre de la bodega origen
 * @param {string} props.locationName - Nombre del local destino
 * @param {string} props.orderType - "cocina" | "barra" (solo para pedidos)
 * @param {string} props.createdAt - Fecha/hora de creación del documento
 * @param {string} props.createdBy - Nombre de quien creó el documento
 * @param {string} props.documentId - ID del pedido o despacho
 * @param {Array} props.items - Lista de productos [{item_name, quantity, unit, supplier}]
 * @param {Function} props.onClose - Callback para cerrar la vista de impresión
 * @param {boolean} props.autoPrint - Si debe ejecutar window.print() automáticamente
 */
const PrintTicket = ({
  type = 'order',
  warehouseName = 'Bodega Central',
  locationName = 'Local',
  orderType = null,
  createdAt,
  createdBy = '',
  documentId = '',
  items = [],
  onClose,
  autoPrint = false
}) => {
  const ticketRef = useRef(null);

  // Formatear fecha en zona horaria de Chile
  const formatChileDateTime = (dateStr) => {
    if (!dateStr) return { date: '-', time: '-' };
    
    const date = new Date(dateStr);
    const chileDate = date.toLocaleDateString('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const chileTime = date.toLocaleTimeString('es-CL', {
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    return { date: chileDate, time: chileTime };
  };

  // Fecha/hora del documento
  const docDateTime = formatChileDateTime(createdAt);
  
  // Fecha/hora actual de impresión
  const printDateTime = formatChileDateTime(new Date().toISOString());

  // Formatear cantidad (sin decimales innecesarios)
  const formatQuantity = (qty) => {
    if (qty === undefined || qty === null) return '0';
    const num = parseFloat(qty);
    return num % 1 === 0 ? Math.floor(num).toString() : num.toFixed(1);
  };

  // Calcular totales
  const totalItems = items.length;
  const totalQuantity = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

  // Agrupar items por proveedor (opcional, para mejor organización)
  const groupedBySupplier = items.reduce((groups, item) => {
    const supplier = item.supplier || 'Sin proveedor';
    if (!groups[supplier]) {
      groups[supplier] = [];
    }
    groups[supplier].push(item);
    return groups;
  }, {});

  // Ordenar proveedores alfabéticamente
  const sortedSuppliers = Object.keys(groupedBySupplier).sort((a, b) => {
    if (a === 'Sin proveedor') return 1;
    if (b === 'Sin proveedor') return -1;
    return a.localeCompare(b, 'es');
  });

  // Ejecutar impresión
  const handlePrint = () => {
    window.print();
  };

  // Auto-imprimir si está habilitado
  useEffect(() => {
    if (autoPrint) {
      const timer = setTimeout(() => {
        handlePrint();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoPrint]);

  // Acortar ID para mostrar (últimos 6 caracteres)
  const shortId = documentId ? documentId.slice(-8).toUpperCase() : 'N/A';

  const isOrder = type === 'order';
  const isShipment = type === 'shipment';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 no-print">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto">
        {/* Controles (no se imprimen) */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between no-print">
          <h3 className="font-semibold text-lg">
            {isOrder ? 'Vista previa - Pedido' : 'Vista previa - Despacho'}
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
            >
              Imprimir
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cerrar
              </button>
            )}
          </div>
        </div>

        {/* Ticket (esto es lo que se imprime) */}
        <div className="p-4">
          <div ref={ticketRef} className="print-ticket print-ticket-preview">
            {/* Encabezado */}
            <div className="print-ticket-header">
              <div className="print-ticket-brand">TUPU STOCK</div>
              <div className="print-ticket-warehouse">{warehouseName.toUpperCase()}</div>
              <div className="print-ticket-title">
                {isOrder ? 'PEDIDO LOCAL' : 'DESPACHO INTERNO'}
              </div>
            </div>

            {/* Metadatos */}
            <div className="print-ticket-meta">
              {isOrder ? (
                <>
                  <div className="print-ticket-meta-full">
                    <span className="print-ticket-meta-label">Local: </span>
                    <span className="print-ticket-meta-value">{locationName}</span>
                  </div>
                  {orderType && (
                    <div className="print-ticket-meta-full">
                      <span className="print-ticket-meta-label">Área: </span>
                      <span className="print-ticket-meta-value" style={{ textTransform: 'uppercase' }}>
                        {orderType}
                      </span>
                    </div>
                  )}
                  <div className="print-ticket-meta-row">
                    <span className="print-ticket-meta-label">Fecha pedido:</span>
                    <span className="print-ticket-meta-value">{docDateTime.date}</span>
                  </div>
                  <div className="print-ticket-meta-row">
                    <span className="print-ticket-meta-label">Hora:</span>
                    <span className="print-ticket-meta-value">{docDateTime.time}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="print-ticket-meta-full">
                    <span className="print-ticket-meta-label">Origen: </span>
                    <span className="print-ticket-meta-value">{warehouseName}</span>
                  </div>
                  <div className="print-ticket-meta-full">
                    <span className="print-ticket-meta-label">Destino: </span>
                    <span className="print-ticket-meta-value">{locationName}</span>
                  </div>
                  <div className="print-ticket-meta-row">
                    <span className="print-ticket-meta-label">Fecha:</span>
                    <span className="print-ticket-meta-value">{docDateTime.date}</span>
                  </div>
                  <div className="print-ticket-meta-row">
                    <span className="print-ticket-meta-label">Hora:</span>
                    <span className="print-ticket-meta-value">{docDateTime.time}</span>
                  </div>
                </>
              )}
            </div>

            <hr className="print-ticket-separator-double" />

            {/* Header de productos */}
            <div className="print-ticket-items-header">
              {isOrder && <span style={{ width: '12mm' }}></span>}
              <span className="print-ticket-items-header-qty">CANT.</span>
              <span className="print-ticket-items-header-product">PRODUCTO</span>
            </div>

            {/* Lista de productos agrupados por proveedor */}
            <div className="print-ticket-items">
              {sortedSuppliers.map((supplier) => (
                <div key={supplier} className="print-ticket-supplier-group">
                  {sortedSuppliers.length > 1 && (
                    <div className="print-ticket-supplier-name">{supplier}</div>
                  )}
                  {groupedBySupplier[supplier]
                    .sort((a, b) => (a.item_name || '').localeCompare(b.item_name || '', 'es'))
                    .map((item, idx) => (
                      <div key={idx} className="print-ticket-item">
                        {isOrder && (
                          <span className="print-ticket-item-checkbox">[ ]</span>
                        )}
                        <span className="print-ticket-item-qty">
                          {formatQuantity(item.quantity)}
                        </span>
                        <span className="print-ticket-item-name">
                          {item.item_name}
                          {item.unit && item.unit !== 'unidad' && item.unit !== 'unid' && (
                            <span className="print-ticket-item-unit"> ({item.unit})</span>
                          )}
                        </span>
                      </div>
                    ))}
                </div>
              ))}
            </div>

            <hr className="print-ticket-separator-double" />

            {/* Totales */}
            <div className="print-ticket-totals">
              <div className="print-ticket-total-row">
                <span className="print-ticket-total-label">Productos distintos:</span>
                <span className="print-ticket-total-value">{totalItems}</span>
              </div>
              <div className="print-ticket-total-row">
                <span className="print-ticket-total-label">Unidades totales:</span>
                <span className="print-ticket-total-value">{formatQuantity(totalQuantity)}</span>
              </div>
            </div>

            {/* Footer con usuario y documento */}
            <div className="print-ticket-footer">
              <div className="print-ticket-footer-row">
                <span>{isOrder ? 'Pedido realizado por:' : 'Preparado por:'}</span>
              </div>
              <div className="print-ticket-footer-row" style={{ fontWeight: 'bold' }}>
                {createdBy || 'Usuario'}
              </div>
              <div className="print-ticket-footer-row" style={{ marginTop: '2mm' }}>
                <span>{isOrder ? 'ID Pedido:' : 'ID Despacho:'}</span>
              </div>
              <div className="print-ticket-doc-id">{shortId}</div>
            </div>

            {/* Zona de firma (solo para despacho) */}
            {isShipment && (
              <div className="print-ticket-signature">
                <div className="print-ticket-signature-title">ENTREGA TRANSPORTE</div>
                <div className="print-ticket-signature-line">
                  Recibe: <span className="print-ticket-signature-blank"></span>
                </div>
                <div className="print-ticket-signature-line">
                  Firma: <span className="print-ticket-signature-blank"></span>
                </div>
                <div className="print-ticket-signature-line">
                  Hora: <span className="print-ticket-signature-blank"></span>
                </div>
              </div>
            )}

            {/* Pie de documento */}
            <div className="print-ticket-doc-footer">
              Documento interno TUPU Stock
            </div>

            {/* Timestamp de impresión */}
            <div className="print-ticket-printed-at">
              Impreso: {printDateTime.date} - {printDateTime.time}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintTicket;
